import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { TLCTemplateActionTypeKey, TLCTemplateKey, TlcTemplateDetailsKey } from '../configs/tlc';
import { createTLCTemplate, getTemplateActionTypes, getTLCTemplateById, updateTLCTemplate, getTlcTemplates, getTlcTemplateDetails, executeTlcTransactions } from '../api/tlc';
import { TemplateActionType, TemplateResponse } from '../@types/tlc.type';
import { QueryOption } from '../@types';
import { TlcTemplatesListResponse, TLCTemplateDetailsDataType } from '../@types/tlc.type';

export const useGetAllTlcTemplates = (queryOption: QueryOption,): UseQueryResult<TlcTemplatesListResponse> => {
  return useQuery({
    queryKey: [TLCTemplateKey, queryOption.filterModel],
    queryFn: () => getTlcTemplates(queryOption)
  });
};

export const useAddTLCTemplate = (): UseMutationResult<TemplateResponse> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await createTLCTemplate(data);
      return response;
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey, response.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey]
      });
    }
  });
};

export const useUpdateTLCTemplate = (id?: number): UseMutationResult<TemplateResponse> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => updateTLCTemplate(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey, id]
      });
    }
  }); 
};

export const useGetTLCTemplateById = (id: number): UseQueryResult<TemplateResponse> => {
  return useQuery({
    queryKey: [TLCTemplateKey, id],
    queryFn: () => id !== 0 && getTLCTemplateById(id),
    enabled: false,
    gcTime: 0
  });
};

export const useGetActionTemplateTypes = (): UseQueryResult<[TemplateActionType]> => {
  return useQuery({
    queryKey: [TLCTemplateActionTypeKey],
    queryFn: () => getTemplateActionTypes()
  });
};
  
export const useGetTlcTemplateDetails = (): UseQueryResult<TLCTemplateDetailsDataType[]> => {
  return useQuery({
    queryKey: [TlcTemplateDetailsKey],
    queryFn: () => getTlcTemplateDetails()
  });
};

export const useExecuteTlcTransactions = (): UseMutationResult => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await executeTlcTransactions(data);
    }
  });
};
